import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, MapPin, CreditCard, AlertTriangle, FileText, ChevronDown, ChevronUp } from 'lucide-react';

interface Section {
  id: string;
  icon: React.ElementType;
  title: string;
  color: string;
  content: React.ReactNode;
}

interface TermsProps {
  /** When supplied the screen behaves as an overlay (used from sign-up, where
   *  navigating away would throw out whatever the rider has already typed). */
  onClose?: () => void;
}

const sections: Section[] = [
  {
    id: 'account',
    icon: FileText,
    title: 'Your Account',
    color: 'text-safaricab-gold',
    content: (
      <ul className="space-y-2 ml-3">
        <li>• You must be at least <strong className="text-white">18 years old</strong> to create a SafariCab account.</li>
        <li>• You are responsible for keeping your login (your phone number and verification codes) secure. Do not share your one-time codes with anyone - SafariCab will never ask for them.</li>
        <li>• The information you provide (name, phone, email) must be accurate and kept up to date.</li>
        <li>• One account per person. Accounts found to be fraudulent or shared may be suspended.</li>
      </ul>
    ),
  },
  {
    id: 'rides',
    icon: CreditCard,
    title: 'Booking & Payment',
    color: 'text-green-400',
    content: (
      <ul className="space-y-2 ml-3">
        <li>• Fares are calculated by the SafariCab pricing engine from <strong className="text-white">distance, time, ride type and any surge</strong>, with a minimum fare per trip. The estimate is shown before you confirm.</li>
        <li>• You can pay by <strong className="text-white">cash or card</strong>. Card payments are processed securely through our payment partner.</li>
        <li>• Additional stops and changes to your drop-off may adjust the fare based on the extra distance and time.</li>
        <li>• Promo and discount codes are subject to their own terms and may be withdrawn at any time.</li>
        <li>• Fare disputes must be raised within <strong className="text-white">48 hours</strong> of the trip via the app or support@safaricab.app.</li>
      </ul>
    ),
  },
  {
    id: 'conduct',
    icon: Shield,
    title: 'Rider Conduct',
    color: 'text-blue-400',
    content: (
      <ul className="space-y-2 ml-3">
        <li>• Treat your driver with <strong className="text-white">respect and courtesy</strong>. Abuse, harassment or discrimination will result in account suspension and may be reported to the authorities.</li>
        <li>• Do not ask a driver to break traffic laws, exceed vehicle capacity, or carry prohibited items.</li>
        <li>• You are responsible for any <strong className="text-white">damage you cause</strong> to a vehicle; cleaning or repair fees may be charged.</li>
        <li>• Repeated last-minute cancellations or no-shows may attract a cancellation fee.</li>
      </ul>
    ),
  },
  {
    id: 'safety',
    icon: MapPin,
    title: 'Safety, Location & Privacy',
    color: 'text-red-400',
    content: (
      <ul className="space-y-2 ml-3">
        <li>• SafariCab processes your data under the <strong className="text-white">Kenya Data Protection Act, 2019</strong>.</li>
        <li>• Your <strong className="text-white">location is used while the app is open</strong> to find nearby drivers, set your pickup, and track your trip. Sharing your live trip with a contact is optional and rider-initiated.</li>
        <li>• Trip records (route, time, fare) are retained for safety, billing and dispute resolution.</li>
        <li>• Your data may be shared with your matched driver (name, pickup, drop-off) and, where required by law, with authorities.</li>
        <li>• Use the in-app <strong className="text-white">emergency / SOS</strong> features if you ever feel unsafe.</li>
      </ul>
    ),
  },
  {
    id: 'liability',
    icon: AlertTriangle,
    title: 'Liability & General',
    color: 'text-orange-400',
    content: (
      <div className="space-y-3">
        <ul className="space-y-2 ml-3">
          <li>• SafariCab connects riders with independent drivers; drivers are not employees of SafariCab.</li>
          <li>• We work to keep the service reliable but do not guarantee a driver will always be available.</li>
          <li>• SafariCab may update these terms; continued use after an update means you accept the changes.</li>
          <li>• These terms are governed by the <strong className="text-white">laws of Kenya</strong>, with disputes resolved in the courts of Nairobi.</li>
        </ul>
        <div className="mt-4 p-3 bg-safaricab-gold/10 rounded-xl border border-safaricab-gold/30">
          <p className="text-safaricab-gold text-xs font-semibold">Last updated: July 2026</p>
          <p className="text-white/50 text-xs mt-1">Questions? support@safaricab.app</p>
        </div>
      </div>
    ),
  },
];

const Terms: React.FC<TermsProps> = ({ onClose }) => {
  const navigate = useNavigate();
  const dismiss = onClose ?? (() => navigate(-1));
  const [open, setOpen] = useState<string | null>('account');

  return (
    <div className="fixed inset-0 z-50 bg-safaricab-darker overflow-y-auto">
      <div className="sticky top-0 z-10 bg-safaricab-darker/95 backdrop-blur px-4 pt-[calc(max(var(--sai-top),14px)+16px)] pb-3 border-b border-white/10">
        <button onClick={dismiss} className="text-white/50 hover:text-white flex items-center gap-2">
          <ArrowLeft className="w-5 h-5" />
          <span className="text-sm">Back</span>
        </button>
      </div>

      <div className="px-4 pt-4 pb-24">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-full bg-safaricab-gold/20 flex items-center justify-center">
            <FileText className="w-6 h-6 text-safaricab-gold" />
          </div>
          <div>
            <h2 className="text-white font-bold text-xl">Terms &amp; Conditions</h2>
            <p className="text-white/40 text-xs">SafariCab Rider - Kenya</p>
          </div>
        </div>

        <div className="space-y-3">
          {sections.map(({ id, icon: Icon, title, color, content }) => (
            <div key={id} className="bg-safaricab-card rounded-xl border border-white/10 overflow-hidden">
              <button onClick={() => setOpen(prev => (prev === id ? null : id))} className="w-full flex items-center gap-3 px-4 py-4 text-left">
                <Icon className={`w-5 h-5 ${color} flex-shrink-0`} />
                <span className="text-white font-semibold text-sm flex-1">{title}</span>
                {open === id ? <ChevronUp className="w-4 h-4 text-white/40" /> : <ChevronDown className="w-4 h-4 text-white/40" />}
              </button>
              {open === id && (
                <div className="px-4 pb-4 border-t border-white/10 pt-3 text-white/70 text-sm leading-relaxed">
                  {content}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Terms;
